import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/Header';
import ReportedProfilesTab from './components/ReportedProfilesTab';
import UserVerificationTab from './components/UserVerificationTab';
import SafetyMonitoringTab from './components/SafetyMonitoringTab';
import SubscriptionManagementTab from './components/SubscriptionManagementTab';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { verificationService } from '../../services/supabaseService';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('reported');
  const [pendingCount, setPendingCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if user is admin (only after profile has loaded so we don't redirect too early)
    const checkAdmin = async () => {
      if (!user?.id) {
        navigate('/registration-login');
        return;
      }

      // Check if user has admin role (is_admin = true in user_profiles)
      const adminStatus = userProfile?.isAdmin === true;
      setIsAdmin(adminStatus);

      // Only redirect non-admins once we have loaded the profile (userProfile != null)
      if (!adminStatus && userProfile != null) {
        navigate('/member-dashboard');
        return;
      }

      if (!adminStatus) return;

      // Load pending verification count
      try {
        const data = await verificationService.getPendingVerifications();
        const count = data?.filter(
          (p) => p?.photoVerificationStatus === 'pending' || p?.identityVerificationStatus === 'pending'
        )?.length || 0;
        setPendingCount(count);
      } catch (err) {
        console.error('Error loading pending count:', err);
      }
    };

    if (!loading && user) {
      checkAdmin();
    }
  }, [user, userProfile, loading, navigate]);

  const tabs = [
    {
      id: 'reported',
      label: 'Reported Profiles',
      icon: 'AlertTriangle',
      badge: 12
    },
    {
      id: 'verification',
      label: 'User Verification',
      icon: 'BadgeCheck',
      badge: pendingCount > 0 ? pendingCount : null
    },
    {
      id: 'subscriptions',
      label: 'Subscriptions',
      icon: 'CreditCard',
      badge: null
    },
    {
      id: 'safety',
      label: 'Safety Monitoring',
      icon: 'Shield',
      badge: null
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Shield" size={48} color="var(--color-error)" className="mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You need admin privileges to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'reported':
        return <ReportedProfilesTab />;
      case 'verification':
        return <UserVerificationTab />;
      case 'safety':
        return <SafetyMonitoringTab />;
      case 'subscriptions':
        return <SubscriptionManagementTab />;
      default:
        return <ReportedProfilesTab />;
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Panel - Bamchik Platform Management</title>
        <meta name="description" content="Admin panel for managing reported profiles, user verification, and platform safety" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="mx-auto max-w-[1400px] px-4 lg:px-6 py-6 md:py-8 lg:py-10">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="Shield" size={24} color="var(--color-primary)" />
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold">
                Admin Panel
              </h1>
            </div>
            <p className="text-sm md:text-base text-muted-foreground">
              Manage platform safety, user verification, reported content, and subscriptions. Use the tabs below — scroll horizontally if a tab is off-screen.
            </p>
          </div>

          <div className="bg-card rounded-xl shadow-warm overflow-hidden">
            <div className="border-b border-border overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex min-w-max">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`relative flex items-center gap-2 px-4 md:px-6 py-4 font-caption font-medium text-sm md:text-base transition-smooth whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'text-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon
                      name={tab?.icon}
                      size={18}
                      color={activeTab === tab?.id ? 'var(--color-primary)' : 'currentColor'}
                    />
                    <span>{tab?.label}</span>
                    {tab?.badge && (
                      <span className="flex items-center justify-center h-5 w-5 rounded-full bg-error text-error-foreground text-xs font-bold">
                        {tab?.badge}
                      </span>
                    )}
                    {activeTab === tab?.id && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 md:p-6 lg:p-8">
              {renderTabContent()}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminPanel;